import { DateTime } from "luxon";
import { z } from "zod";

export const DAYS_OF_THE_WEEK = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"] as const;
export type DayOfTheWeek = (typeof DAYS_OF_THE_WEEK)[number];
export const DaysOfTheWeekSchema = z.array(z.enum(DAYS_OF_THE_WEEK)).min(1);
export type DaysOfTheWeek = z.infer<typeof DaysOfTheWeekSchema>;

/**
 * @deprecated
 * Use DaysOfTheWeekSchema.decode() or DaysOfTheWeekSchema.parse()
 */
export function makeDaysOfTheWeek(value: string[]): DaysOfTheWeek {
  return DaysOfTheWeekSchema.parse(value);
}

export const HourSchema = z.number().int().min(0).max(24).brand("Hour");

export const MinuteSchema = z.number().int().min(0).max(59).brand("Minute");

export const YearSchema = z.number().int().min(1).brand("Year");

export const MonthSchema = z.number().int().min(1).max(12).brand("Month");

export const DaySchema = z.number().int().min(1).max(31).brand("Day");

export const TimeRecordSchema = z
  .object({
    hour: HourSchema,
    minute: MinuteSchema,
  })
  .refine((data) => data.hour < 24 || (data.hour === 24 && data.minute === 0), {
    message: "24:00 is the maximum time representable",
  })
  .brand("TimeRecord");
export type TimeRecord = z.infer<typeof TimeRecordSchema>;

export function makeTimeRecord(hour: number, minute: number): TimeRecord {
  return TimeRecordSchema.parse({
    hour,
    minute,
  });
}

export const DateRecordSchema = z.object({
  year: YearSchema,
  month: MonthSchema,
  day: DaySchema,
});
export type DateRecord = z.infer<typeof DateRecordSchema>;

export function makeDateRecord(year: number, month: number, day: number): DateRecord {
  return DateRecordSchema.parse({
    year,
    month,
    day,
  });
}

export const DateTimeRecordSchema = z.intersection(DateRecordSchema, TimeRecordSchema);
export type DateTimeRecord = z.infer<typeof DateTimeRecordSchema>;

export function makeDateTimeRecord(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
): DateTimeRecord {
  return DateTimeRecordSchema.parse({
    year,
    month,
    day,
    hour,
    minute,
  });
}

export const TimeRangeSchema = z.object({
  kind: z.literal("time_range"),
  start: TimeRecordSchema,
  end: TimeRecordSchema,
});
export type TimeRange = z.infer<typeof TimeRangeSchema>;

export function makeTimeRange(start: TimeRecord, end: TimeRecord): TimeRange {
  return TimeRangeSchema.parse({
    kind: "time_range",
    start,
    end,
  });
}

export const AllDayTimeRangeSchema = z.object({
  kind: z.literal("all_day"),
});
export type AllDayTimeRange = z.infer<typeof AllDayTimeRangeSchema>;

/**
 * @deprecated
 * Use makeAllDayEffectiveRange()
 */
export function makeAllDayTimeRange(): AllDayTimeRange {
  return { kind: "all_day" };
}

export const EffectiveTimeRangesSchema = z.union([
  z.tuple([AllDayTimeRangeSchema]),
  z.tuple([TimeRangeSchema]).rest(TimeRangeSchema),
]);
export type EffectiveTimeRanges = z.infer<typeof EffectiveTimeRangesSchema>;

/**
 * @deprecated
 * Use EffectiveTimeRangesSchema.decode() or EffectiveTimeRangesSchema.parse()
 */
export function makeEffectiveTimeRanges(value: EffectiveTimeRanges): EffectiveTimeRanges {
  return EffectiveTimeRangesSchema.parse(value);
}

export function makeAllDayEffectiveRange(): EffectiveTimeRanges {
  return [{ kind: "all_day" }];
}

export type TimeZone = string;
export const TimeZoneSchema = z.string().min(1);

declare const LocalisedDateBrand: unique symbol;
export type LocalisedDate = DateTime & { readonly [LocalisedDateBrand]: true };
export function dateRecordToLocalisedDate(d: DateRecord, tz: TimeZone) {
  return DateTime.fromObject(d, { zone: tz }) as LocalisedDate;
}

declare const LocalisedDateTimeBrand: unique symbol;
export type LocalisedDateTime = DateTime & { readonly [LocalisedDateTimeBrand]: true };
export function dateTimeRecordToLocalisedDateTime(d: DateTimeRecord, tz: TimeZone) {
  return DateTime.fromObject(d, { zone: tz }) as LocalisedDateTime;
}

export function getLocalisedNow(timezone: TimeZone) {
  return DateTime.now().setZone(timezone) as LocalisedDateTime;
}

// Minutes from midnight can sometimes be a convenient numeric value for representing
// and manipulating a day's hour+time. Note that it should not be relied upon in all
// cases. During DST transitions, the value may not correlate to the same fixed point in
// time.
// Example: A normal day may have 24*60 minutes, but a day with a DST spring forward may
// have 23*60 minutes, while a day with a DST fall back may have 25*60
// minutes.
// Example: On most days a MinutesFromMidnight of 150 will mean 2:30am. However, if
// DST springs forward at 2am by one hour, then 2-3am does not exist on that day and 150 minutes from
// midnight will mean 3:30am in local time.
declare const MinutesFromMidnightBrand: unique symbol;
export type MinutesFromMidnight = number & { readonly [MinutesFromMidnightBrand]: true };

export const MINUTES_FROM_MIDNIGHT_END_OF_DAY = 24 * 60; // 24:00
export const MIN_MINUTES_FROM_MIDNIGHT = 0; // 00:00
export const MAX_MINUTES_FROM_MIDNIGHT = MINUTES_FROM_MIDNIGHT_END_OF_DAY;
const MinutesFromMidnightSchema = z.number().int().min(MIN_MINUTES_FROM_MIDNIGHT).max(MAX_MINUTES_FROM_MIDNIGHT);

export function makeMinutesFromMidnight(value: number): MinutesFromMidnight {
  return MinutesFromMidnightSchema.parse(value) as MinutesFromMidnight;
}

function _recordToMinutesFromMidnight(val: TimeRecord | DateTimeRecord) {
  return (val.hour * 60 + val.minute) as MinutesFromMidnight;
}

export function timeRecordToMinutesFromMidnight(t: TimeRecord) {
  return _recordToMinutesFromMidnight(t);
}

export function dateTimeRecordToMinutesFromMidnight(dt: DateTimeRecord) {
  return _recordToMinutesFromMidnight(dt);
}

export function dateTimeToMinutesFromMidnight(dt: DateTime | LocalisedDate) {
  return _recordToMinutesFromMidnight(dateTimeToDateTimeRecord(dt));
}

export function minutesFromMidnightToTimeRecord(value: MinutesFromMidnight | number) {
  const val = makeMinutesFromMidnight(value);
  return TimeRecordSchema.decode({ hour: Math.floor(val / 60), minute: val % 60 });
}

export function dateTimeToDayOfWeek(dt: DateTime | LocalisedDate | LocalisedDateTime) {
  // Provides a textual representation for a date's day of the week.
  // Example: "MON", "TUE", etc
  return dt.toFormat("ccc").toUpperCase() as DayOfTheWeek;
}

export function dateTimeToDateRecord(dt: DateTime | LocalisedDate | LocalisedDateTime) {
  return DateRecordSchema.decode(dt);
}

export function dateTimeToDateTimeRecord(dt: DateTime | LocalisedDate | LocalisedDateTime) {
  return DateTimeRecordSchema.decode(dt);
}

export function dateRecordToDateTimeRecord(date: DateRecord) {
  return dateRecordToStartOfDay(date);
}

export function dateTimeRecordToDateRecord(date: DateTimeRecord) {
  return DateRecordSchema.decode(date);
}

export function dateRecordToStartOfDay(date: DateRecord) {
  return DateTimeRecordSchema.decode({ ...date, hour: 0, minute: 0 });
}

export function dateRecordToEndOfDay(date: DateRecord) {
  return DateTimeRecordSchema.decode({ ...date, hour: 24, minute: 0 });
}

export function isoToDateTime(iso: string) {
  return DateTime.fromISO(
    iso,
    // Use the timezone in the timestamp for localisation, otherwise it will fallback to
    // the system's timezone
    { setZone: true },
  );
}

export function jsDateToDateTime(date: Date) {
  // When querying time values (days, hours, minutes, etc) a JS date may use the local
  // system's timezone, which can cause inconsistencies across systems. Luxon has a similar
  // behaviour by default.
  //
  // As we are mostly doing operations in a local time that is distinct from our the system
  // timezone, we need to ensure that the same timezone is used given what was passed in.
  // The workaround is to serialise the date back to ISO to retain the UTC offset, then Luxon
  // can derive the DateTime's zone from the timestamp.
  return DateTime.fromISO(date.toISOString(), { setZone: true });
}

/**
 * Compare two DateTimeRecords lexicographically.
 * Returns negative if a < b, positive if a > b, 0 if equal.
 */
export function compareDateTimeRecords(a: DateTimeRecord, b: DateTimeRecord): number {
  if (a.year !== b.year) return a.year - b.year;
  if (a.month !== b.month) return a.month - b.month;
  if (a.day !== b.day) return a.day - b.day;
  if (a.hour !== b.hour) return a.hour - b.hour;
  return a.minute - b.minute;
}
